const CURRENCY = "INR";

export const initializeDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

export const pushEvent = (eventName, data = {}) => {
  initializeDataLayer();

  window.dataLayer.push({
    event: eventName,
    ...data,
  });
};

const normalizeItem = (product, index = 0) => {
  if (!product) return null;

  const price = Number(
    product.rentalPrice ??
    product.rentPrice ??
    product.price ??
    product.salePrice ??
    0
  );

  return {
    item_id: String(product._id ?? product.id ?? ""),
    item_name: product.name ?? product.productName ?? "Product",
    item_brand: "Hindustan Wardrobe",
    item_category:
      product.categoryName ??
      product.category?.name ??
      product.category ??
      undefined,
    item_category2:
      product.subCategoryName ??
      product.subCategory?.name ??
      product.subCategory ??
      undefined,
    item_variant:
      product.variantName ??
      product.variant ??
      undefined,
    price,
    quantity: Number(product.quantity ?? 1),
    index,
  };
};

export const trackPageView = ({
  pageTitle,
  pageLocation = window.location.href,
  pagePath = window.location.pathname,
} = {}) => {
  pushEvent("page_view", {
    page_title: pageTitle || document.title,
    page_location: pageLocation,
    page_path: pagePath,
  });
};

export const trackViewItem = (product) => {
  const item = normalizeItem(product);

  if (!item) return;

  pushEvent("view_item", {
    ecommerce: {
      currency: CURRENCY,
      value: item.price,
      items: [item],
    },
  });
};

export const trackSelectItem = (product, listName = "Products") => {
  const item = normalizeItem(product);

  if (!item) return;

  pushEvent("select_item", {
    ecommerce: {
      item_list_name: listName,
      items: [item],
    },
  });
};

export const trackSearch = (searchTerm) => {
  if (!searchTerm) return;

  pushEvent("search", {
    search_term: searchTerm,
  });
};

export const trackAddToCart = (product, quantity = 1) => {
  const item = normalizeItem({
    ...product,
    quantity,
  });

  if (!item) return;

  pushEvent("add_to_cart", {
    ecommerce: {
      currency: CURRENCY,
      value: item.price * quantity,
      items: [item],
    },
  });
};

export const trackRemoveFromCart = (product, quantity = 1) => {
  const item = normalizeItem({
    ...product,
    quantity,
  });

  if (!item) return;

  pushEvent("remove_from_cart", {
    ecommerce: {
      currency: CURRENCY,
      value: item.price * quantity,
      items: [item],
    },
  });
};

export const trackBeginCheckout = ({
  items = [],
  value = 0,
  coupon = undefined,
} = {}) => {
  const normalizedItems = items
    .map((item, index) => normalizeItem(item, index))
    .filter(Boolean);

  pushEvent("begin_checkout", {
    ecommerce: {
      currency: CURRENCY,
      value: Number(value),
      coupon,
      items: normalizedItems,
    },
  });
};

export const trackAddPaymentInfo = ({
  items = [],
  value = 0,
  paymentType,
} = {}) => {
  const normalizedItems = items
    .map((item, index) => normalizeItem(item, index))
    .filter(Boolean);

  pushEvent("add_payment_info", {
    ecommerce: {
      currency: CURRENCY,
      value: Number(value),
      payment_type: paymentType,
      items: normalizedItems,
    },
  });
};

export const trackPurchase = ({
  transactionId,
  items = [],
  value = 0,
  tax = 0,
  shipping = 0,
  coupon,
} = {}) => {
  if (!transactionId) {
    console.warn("Purchase tracking skipped: transactionId is missing.");
    return;
  }

  const normalizedItems = items
    .map((item, index) => normalizeItem(item, index))
    .filter(Boolean);

  pushEvent("purchase", {
    ecommerce: {
      transaction_id: String(transactionId),
      currency: CURRENCY,
      value: Number(value),
      tax: Number(tax),
      shipping: Number(shipping),
      coupon,
      items: normalizedItems,
    },
  });
};

export const trackRentalProductView = (product) => {
  const item = normalizeItem(product);

  if (!item) return;

  pushEvent("view_rental_product", {
    ecommerce: {
      currency: CURRENCY,
      value: item.price,
      items: [item],
    },
  });
};

export const trackRentalDateSelected = ({
  product,
  startDate,
  endDate,
} = {}) => {
  const item = normalizeItem(product);

  pushEvent("select_rental_date", {
    rental: {
      start_date: startDate,
      end_date: endDate,
      item: item,
    },
  });
};

export const trackAvailabilityCheck = ({
  product,
  startDate,
  endDate,
} = {}) => {
  const item = normalizeItem(product);

  pushEvent("check_availability", {
    rental: {
      start_date: startDate,
      end_date: endDate,
      item: item,
    },
  });
};

export const trackBookingStarted = ({
  items = [],
  value = 0,
} = {}) => {
  pushEvent("start_booking", {
    ecommerce: {
      currency: CURRENCY,
      value: Number(value),
      items: items
        .map((item, index) => normalizeItem(item, index))
        .filter(Boolean),
    },
  });
};

export const trackBookingCompleted = ({
  bookingId,
  items = [],
  value = 0,
} = {}) => {
  if (!bookingId) return;

  pushEvent("booking_completed", {
    booking: {
      booking_id: String(bookingId),
    },
    ecommerce: {
      transaction_id: String(bookingId),
      currency: CURRENCY,
      value: Number(value),
      items: items
        .map((item, index) => normalizeItem(item, index))
        .filter(Boolean),
    },
  });
};